import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";
import useIsOnline, { OfflineViewingCachedDataBanner } from "../useIsOnline";

export default function Teams() {
  const isOnline = useIsOnline();
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [event, setEvent] = useState(null);
  const [teamAName, setTeamAName] = useState("Team A");
  const [teamBName, setTeamBName] = useState("Team B");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [activeTeam, setActiveTeam] = useState("A");

  // Genuine team-vs-team formats only — pair formats (Better Ball,
  // Fourball, Foursomes, Greensomes) use the Pairs page instead, generated
  // automatically by Auto Group, not this rival-side naming workflow.
  const TEAM_FORMATS = ['Team Stableford', 'Team Medal', 'Team Aggregate Stableford', 'Team Aggregate Stroke Play', 'Team Match Play'];

  useEffect(() => { loadTeams(); }, []);

  const loadTeams = async () => {
    try {
      const res = await api.get("/teams");
      setEvent(res.data.event);
      setPlayers(res.data.players || []);
      if (res.data.teams.length >= 2) {
        setTeamAName(res.data.teams[0].team_name);
        setTeamBName(res.data.teams[1].team_name);
        setTeams(res.data.teams);
      }
    } catch(e) {
      setError("Failed to load teams");
    } finally { setLoading(false); }
  };

  const saveTeams = async () => {
    try {
      const res = await api.post("/teams/setup", { teamAName, teamBName });
      setTeams([res.data.teamA, res.data.teamB]);
      setSaved("Teams saved!");
      setTimeout(() => setSaved(""), 3000);
    } catch(e) { setError("Failed to save team names"); }
  };

  const assignPlayer = async (playerId, teamId) => {
    try {
      await api.post("/teams/assign", { playerId, teamId });
      setPlayers(prev => prev.map(p => p.player_id === playerId ? { ...p, team_id: teamId } : p));
    } catch(e) { console.error("Assign error:", e.message); }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all team assignments?")) return;
    try {
      await api.post("/teams/clearall");
      setPlayers(prev => prev.map(p => ({ ...p, team_id: null })));
    } catch(e) { setError("Failed to clear"); }
  };

  const teamA = teams[0];
  const teamB = teams[1];
  const teamACount = players.filter(p => teamA && p.team_id === teamA.id).length;
  const teamBCount = players.filter(p => teamB && p.team_id === teamB.id).length;
  const unassignedCount = players.filter(p => !p.team_id).length;

  const groups = {};
  players.forEach(p => {
    const g = p.grp || 0;
    if (!groups[g]) groups[g] = [];
    groups[g].push(p);
  });

  const getGroupBalance = (grpPlayers) => {
    const a = grpPlayers.filter(p => teamA && p.team_id === teamA.id).length;
    const b = grpPlayers.filter(p => teamB && p.team_id === teamB.id).length;
    return { a, b };
  };

  return (
    <Layout>
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {!isOnline && <OfflineViewingCachedDataBanner screen="Teams" />}

        {/* Header */}
        <div style={{ backgroundColor: "#f0fdf4", borderBottom: "1px solid #d1d5db", padding: "8px 16px", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <span style={{ fontWeight: "600", color: "#14532d", fontSize: "14px" }}>Teams</span>
          {event && <span style={{ color: "#6b7280", fontSize: "12px" }}>Game: {event.game_type}</span>}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "16px" }}>
            <span style={{ fontSize: "13px", fontWeight: "600" }}>Assign to:</span>
            <input type="text" value={teamAName} onChange={e => setTeamAName(e.target.value)} onFocus={() => setActiveTeam("A")} onKeyDown={e => e.key === "Enter" && saveTeams()}
              placeholder="e.g. Bromsgrove"
              style={{ border: activeTeam === "A" ? "2px solid #2563eb" : "2px solid #93c5fd", backgroundColor: activeTeam === "A" ? "#2563eb" : "white", color: activeTeam === "A" ? "white" : "#1e3a8a", borderRadius: "6px", padding: "6px 12px", fontSize: "13px", fontWeight: "700", width: "130px" }} />
            <input type="text" value={teamBName} onChange={e => setTeamBName(e.target.value)} onFocus={() => setActiveTeam("B")} onKeyDown={e => e.key === "Enter" && saveTeams()}
              placeholder="e.g. Ombersley"
              style={{ border: activeTeam === "B" ? "2px solid #dc2626" : "2px solid #fca5a5", backgroundColor: activeTeam === "B" ? "#dc2626" : "white", color: activeTeam === "B" ? "white" : "#7f1d1d", borderRadius: "6px", padding: "6px 12px", fontSize: "13px", fontWeight: "700", width: "130px" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ backgroundColor: "#2563eb", color: "white", fontSize: "12px", fontWeight: "600", padding: "4px 10px", borderRadius: "4px" }}>{teamAName}: {teamACount}</span>
            <span style={{ backgroundColor: "#dc2626", color: "white", fontSize: "12px", fontWeight: "600", padding: "4px 10px", borderRadius: "4px" }}>{teamBName}: {teamBCount}</span>
            {unassignedCount > 0 && <span style={{ backgroundColor: "#f97316", color: "white", fontSize: "12px", fontWeight: "600", padding: "4px 10px", borderRadius: "4px" }}>Unassigned: {unassignedCount}</span>}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
            <button onClick={clearAll} style={{ backgroundColor: "#6b7280", color: "white", border: "none", padding: "6px 24px", borderRadius: "4px", fontWeight: "600", cursor: "pointer", fontSize: "13px" }}>Clear All</button>
            <button onClick={saveTeams} style={{ backgroundColor: "#16a34a", color: "white", border: "none", padding: "6px 24px", borderRadius: "4px", fontWeight: "600", cursor: "pointer", fontSize: "13px" }}>Save Teams</button>
          </div>
        </div>

        {event && !TEAM_FORMATS.includes(event.game_type) && (
          <div style={{ backgroundColor: "#fef9c3", color: "#92400e", padding: "12px 16px", fontSize: "14px", fontWeight: "500" }}>
            ⚠️ Teams are not available for {event.game_type}. Teams are only used for genuine team-vs-team formats (Team Stableford, Team Medal, Team Aggregate, Team Match Play). Better Ball, Fourball, Foursomes and Greensomes use the Pairs page instead — pairs are generated automatically by Auto Group. Texas Scramble is scored per playing group — no named teams needed.
          </div>
        )}
        {saved && <div style={{position:"fixed",top:"20px",left:"50%",transform:"translateX(-50%)",backgroundColor:"#dcfce7",color:"#14532d",padding:"10px 22px",borderRadius:"8px",fontWeight:"700",fontSize:"14px",zIndex:9999,boxShadow:"0 4px 12px rgba(0,0,0,0.2)"}}>{saved}</div>}
        {error && <div style={{ backgroundColor: "#fef2f2", color: "#dc2626", padding: "8px 16px", fontSize: "14px" }}>{error}</div>}

        {/* Instructions */}
        <div style={{ backgroundColor: "#e0f2fe", borderBottom: "1px solid #bae6fd", padding: "10px 16px" }}>
          <span style={{ fontSize: "14px", fontWeight: "600", color: "#0369a1" }}>
            ➚</span>
          <span style={{ fontSize: "14px", color: "#0369a1" }}>
            &nbsp;Step 1: Type each team's name into the two <b>Assign to</b> boxes above and press Enter or click <b>Save Teams</b>.&nbsp;&nbsp;
            Step 2: Click a box to make it the active team, then click a player to add them. Click a player again to remove them.
          </span>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading && <div style={{ textAlign: "center", padding: "32px", color: "#6b7280" }}>Loading...</div>}
          {!loading && players.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px", color: "#6b7280" }}>No playing players.</div>
          )}
          {Object.entries(groups).sort((a,b) => parseInt(a[0]) - parseInt(b[0])).map(([grp, grpPlayers]) => {
            const bal = getGroupBalance(grpPlayers);
            const allAssigned = grpPlayers.every(p => p.team_id);
            const balanced = bal.a === bal.b && allAssigned;
            const unbalanced = allAssigned && bal.a !== bal.b;
            return (
              <div key={grp}>
                <div style={{ backgroundColor: "#f3f4f6", padding: "6px 16px", borderBottom: "1px solid #d1d5db", borderTop: "1px solid #d1d5db", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontWeight: "700", color: "#374151", fontSize: "14px" }}>
                    {parseInt(grp) === 0 ? "UNASSIGNED" : "GROUP " + grp}
                  </span>
                  {balanced && <span style={{ color: "#16a34a", fontSize: "12px" }}>➉ balanced ({bal.a}/{bal.b})</span>}
                  {unbalanced && <span style={{ color: "#f97316", fontSize: "12px" }}>⚠️ unbalanced ({bal.a}/{bal.b})</span>}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    {grpPlayers.map((player, i) => {
                      const isA = teamA && player.team_id === teamA.id;
                      const isB = teamB && player.team_id === teamB.id;
                      const pid = player.player_id || player.id;
                      return (
                        <tr key={pid} style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: i % 2 === 0 ? "white" : "#f0fdf4" }}>
                          <td style={{ padding: "8px 16px", width: "240px", fontWeight: "500", fontSize: "14px" }}>
                            {player.captain && <span style={{ backgroundColor: "#fbbf24", color: "#78350f", fontSize: "11px", fontWeight: "700", padding: "1px 6px", borderRadius: "4px", marginRight: "6px" }}>C</span>}
                            {player.name}
                          </td>
                          <td style={{ padding: "8px 16px", width: "80px", color: "#6b7280", fontSize: "13px" }}>HCP {player.handicap}</td>
                          <td style={{ padding: "8px 16px" }}>
                            {(() => {
                              const activeTeamObj = activeTeam === "A" ? teamA : teamB;
                              const isActiveAssigned = activeTeamObj && player.team_id === activeTeamObj.id;
                              const label = isA ? teamAName : isB ? teamBName : "";
                              const bg = isA ? "#2563eb" : isB ? "#dc2626" : "#e5e7eb";
                              const color = isA || isB ? "white" : "#374151";
                              return (
                                <button onClick={() => assignPlayer(pid, isActiveAssigned ? null : (activeTeamObj ? activeTeamObj.id : null))}
                                  style={{ backgroundColor: bg, color, border: "none", padding: "6px 20px", borderRadius: "6px", fontWeight: "600", cursor: "pointer", fontSize: "13px", minWidth: "140px" }}>
                                  {label}
                                </button>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
